import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Button, Alert, Stack } from '@weave-mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Link, } from '@mui/material';
import { api } from '../api/apiClient';
import { queryKeys } from '../query/queryClient';
import RelativeTime from '../components/RelativeTime';
const CHANGE_TYPE_LABELS = {
    TRIGGER_MIGRATION: 'Trigger Migration',
    TRIGGER_VALIDATION: 'Trigger Validation',
    ROLLBACK: 'Rollback',
    BATCH_CREATED: 'Batch Created',
    UPDATE_BATCH_STATUS: 'Update Status',
    UPDATE_SETTINGS: 'Update Settings',
    SYNC_HUB_STATUS: 'Sync Hub Status',
    SYNC_CE_STATUS: 'Sync CE Status',
    SYNC_MIGRATION_STATS: 'Sync Migration Stats',
    CLOSE_BATCH: 'Close Batch',
    EXCLUDE_COLLECTIONS: 'Exclude Collections',
    UPDATE_BATCH_DESCRIPTION: 'Update Description',
};
const CHANGE_TYPE_COLORS = {
    TRIGGER_MIGRATION: 'primary',
    TRIGGER_VALIDATION: 'secondary',
    ROLLBACK: 'warning',
    BATCH_CREATED: 'success',
    UPDATE_BATCH_STATUS: 'info',
    UPDATE_SETTINGS: 'default',
    SYNC_HUB_STATUS: 'default',
    SYNC_CE_STATUS: 'default',
    SYNC_MIGRATION_STATS: 'default',
    CLOSE_BATCH: 'info',
    EXCLUDE_COLLECTIONS: 'warning',
    UPDATE_BATCH_DESCRIPTION: 'default',
};
function DetailsCell({ record }) {
    const { changeType, details } = record;
    const parts = [];
    if (details.jobId) {
        parts.push(_jsxs("span", { children: ["Job", ' ', _jsx(Link, { component: RouterLink, to: `/jobs/${details.jobId}`, children: details.jobId })] }, "job"));
    }
    if (details.batchId) {
        parts.push(_jsxs("span", { children: ["Batch", ' ', _jsx(Link, { component: RouterLink, to: `/batches/${details.batchId}`, children: details.batchId })] }, "batch"));
    }
    if (changeType === 'UPDATE_BATCH_STATUS' && details.oldValue !== undefined) {
        parts.push(_jsxs("span", { children: [String(details.oldValue), " \u2192 ", String(details.newValue)] }, "status"));
    }
    if (changeType === 'UPDATE_BATCH_DESCRIPTION') {
        parts.push(_jsxs("span", { children: [details.oldValue !== null && details.oldValue !== undefined
                    ? `was: "${String(details.oldValue)}"`
                    : 'was: (empty)', ' → ', details.newValue !== null && details.newValue !== undefined
                    ? `now: "${String(details.newValue)}"`
                    : 'now: (empty)'] }, "desc"));
    }
    if (changeType === 'EXCLUDE_COLLECTIONS' && details.excludedCount !== undefined) {
        parts.push(_jsxs("span", { children: [details.excludedCount, " collections excluded"] }, "excluded"));
    }
    if (changeType === 'UPDATE_SETTINGS' && details.oldValue && details.newValue) {
        const oldVal = details.oldValue;
        const newVal = details.newValue;
        const changedKeys = Object.keys(newVal).filter((k) => JSON.stringify(oldVal[k]) !== JSON.stringify(newVal[k]));
        if (changedKeys.length > 0) {
            parts.push(_jsx("span", { children: changedKeys.map((k) => `${k}: ${String(oldVal[k])} → ${String(newVal[k])}`).join(', ') }, "settings"));
        }
    }
    if (parts.length === 0)
        return null;
    return (_jsx(Stack, { direction: "row", spacing: 1, flexWrap: "wrap", useFlexGap: true, children: parts.map((part, i) => (_jsxs(React.Fragment, { children: [i > 0 && _jsx("span", { children: "," }), part] }, i))) }));
}
const PAGE_LIMIT = 50;
const ChangeHistory = () => {
    const [cursorStack, setCursorStack] = useState([null]);
    const currentCursor = cursorStack[cursorStack.length - 1];
    const queryParams = {
        limit: PAGE_LIMIT,
        ...(currentCursor ? { cursorState: currentCursor } : {}),
    };
    const { data, isLoading, isError, error } = useQuery({
        queryKey: queryKeys.changes.list(queryParams),
        queryFn: () => api.changes.list(queryParams),
    });
    const handleNext = () => {
        if (data?.pagination.nextCursorState) {
            setCursorStack((prev) => [...prev, data.pagination.nextCursorState]);
        }
    };
    const handlePrev = () => {
        if (cursorStack.length > 1) {
            setCursorStack((prev) => prev.slice(0, -1));
        }
    };
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "h5", sx: { fontWeight: 600 }, gutterBottom: true, children: "Change History" }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3 }, children: "Audit trail of all significant actions performed in the system." }), isError && (_jsxs(Alert, { severity: "error", sx: { mb: 2 }, children: ["Failed to load change history: ", error?.message ?? 'Unknown error'] })), _jsx(TableContainer, { component: Paper, variant: "outlined", children: _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: { fontWeight: 600 }, children: "Timestamp" }), _jsx(TableCell, { sx: { fontWeight: 600 }, children: "Author" }), _jsx(TableCell, { sx: { fontWeight: 600 }, children: "Change Type" }), _jsx(TableCell, { sx: { fontWeight: 600 }, children: "Details" })] }) }), _jsx(TableBody, { children: isLoading ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, align: "center", sx: { py: 4 }, children: _jsx(CircularProgress, { size: 32 }) }) })) : data?.results.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, align: "center", sx: { py: 4 }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: "No changes recorded yet." }) }) })) : (data?.results.map((record) => (_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { sx: { whiteSpace: 'nowrap' }, children: _jsx(RelativeTime, { value: record.createdAt, variant: "body2" }) }), _jsx(TableCell, { children: record.createdBy }), _jsx(TableCell, { children: _jsx(Chip, { label: CHANGE_TYPE_LABELS[record.changeType] ?? record.changeType, color: CHANGE_TYPE_COLORS[record.changeType] ?? 'default', size: "small" }) }), _jsx(TableCell, { children: _jsx(DetailsCell, { record: record }) })] }, record.changeId)))) })] }) }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { mt: 2 }, justifyContent: "flex-end", children: [_jsx(Button, { variant: "outlined", size: "small", onClick: handlePrev, disabled: cursorStack.length <= 1, children: "Previous" }), _jsx(Button, { variant: "outlined", size: "small", onClick: handleNext, disabled: !data?.pagination.nextCursorState, children: "Next" })] })] }));
};
export default ChangeHistory;
